/**
 * Shortcuts Mixin
 * 
 * Adds keyboard shortcut functionality to Stimulus controllers. Controllers using this mixin
 * can register keyboard shortcuts that trigger custom actions when pressed.
 * 
 * Usage:
 *   1. Apply mixin: export default withShortcuts(YourController)
 *   2. Add shortcut-key-value to your element: data-your-controller-shortcut-key-value="i"
 *   3. Override onShortcut(event) method to handle the shortcut
 * 
 * Example:
 *   class ExpandableController extends Controller {
 *     onShortcut(event) {
 *       this.toggle()
 *     }
 *   }
 *   export default withShortcuts(ExpandableController)
 */
export const ShortcutsMixin = {
  shortcutsOutlets: ["components--shortcuts-manager"],
  shortcutsValues: { shortcutKey: String },

  componentsShortcutsManagerOutletConnected(outlet, element) {
    this.shortcutsManager = outlet
    this._registerShortcuts()
  },

  componentsShortcutsManagerOutletDisconnected(outlet, element) {
    this.shortcutsManager = null
  },

  // Override this method in your controller to handle shortcut events
  onShortcut(event) {
  },

  _tryRegisterShortcuts() {
    if (this.shortcutsManager) {
      this._registerShortcuts()
    } else if (window.shortcutsManager && this.hasShortcutKeyValue) {
      this.shortcutsManager = window.shortcutsManager
      this._registerShortcuts()
    } else if (this.hasShortcutKeyValue) {
      setTimeout(() => {
        if (window.shortcutsManager && !this.shortcutsManager) {
          this.shortcutsManager = window.shortcutsManager
          this._registerShortcuts()
        }
      }, 100)
    }
  },

  _registerShortcuts() {
    if (!this.shortcutsManager || !this.hasShortcutKeyValue) return

    this.shortcutsManager.registerShortcut(this.shortcutKeyValue, true, "shortcut-triggered", this.element)
    this._boundOnShortcut = this.onShortcut.bind(this)
    this.element.addEventListener("shortcut:shortcut-triggered", this._boundOnShortcut)
  },

  _unregisterShortcuts() {
    if (!this.shortcutsManager || !this.hasShortcutKeyValue) return

    this.shortcutsManager.unregisterShortcut(this.shortcutKeyValue, true)
    if (this._boundOnShortcut) {
      this.element.removeEventListener("shortcut:shortcut-triggered", this._boundOnShortcut)
      this._boundOnShortcut = null
    }
  }
}

export function withShortcuts(ControllerClass) {
  ControllerClass.outlets = [...(ControllerClass.outlets || []), ...ShortcutsMixin.shortcutsOutlets]
  ControllerClass.values = { ...(ControllerClass.values || {}), ...ShortcutsMixin.shortcutsValues }

  const originalConnect = ControllerClass.prototype.connect
  const originalDisconnect = ControllerClass.prototype.disconnect
  const originalOnShortcut = ControllerClass.prototype.onShortcut

  Object.getOwnPropertyNames(ShortcutsMixin).forEach(name => {
    if (name !== "shortcutsOutlets" && name !== "shortcutsValues") {
      ControllerClass.prototype[name] = ShortcutsMixin[name]
    }
  })

  ControllerClass.prototype.connect = function() {
    if (originalConnect) originalConnect.call(this)
    this._tryRegisterShortcuts()
  }

  ControllerClass.prototype.disconnect = function() {
    this._unregisterShortcuts()
    if (originalDisconnect) originalDisconnect.call(this)
  }

  if (originalOnShortcut) {
    ControllerClass.prototype.onShortcut = originalOnShortcut
  }

  return ControllerClass
}
